import { useState } from "react";
import { useLocalization } from "@fluent/react";
import { Button, Checkbox, Chip, Popover, Row } from "@narmi/design_system";

import type { TokenState } from "./types";

const STATE_OPTIONS: TokenState[] = ["active", "inactive"];

/**
 * Filter chip for the token list. Lets the user narrow the list by token
 * state (active / inactive). An empty selection means "show all".
 */
const TokenStateFilter = ({
  selectedStates,
  onChange,
}: {
  selectedStates: Set<TokenState>;
  onChange: (next: Set<TokenState>) => void;
}) => {
  const { l10n } = useLocalization();
  const [isOpen, setIsOpen] = useState(false);

  const stateLabels: Record<TokenState, string> = {
    active: l10n.getString("filter-state-active"),
    inactive: l10n.getString("filter-state-inactive"),
  };

  const toggleState = (state: TokenState) => {
    const next = new Set(selectedStates);
    if (next.has(state)) {
      next.delete(state);
    } else {
      next.add(state);
    }
    onChange(next);
  };

  const popoverContent = (
    <div className="padding--all--s">
      <div
        role="group"
        aria-label={l10n.getString("filter-state-label")}
        style={{ display: "flex", flexDirection: "column", gap: "12px" }}
      >
        {STATE_OPTIONS.map((state) => (
          <Checkbox
            key={state}
            label={stateLabels[state]}
            kind="condensed"
            checked={selectedStates.has(state)}
            onChange={() => toggleState(state)}
            testId={`token-filter-${state}`}
          />
        ))}
      </div>
      <div className="padding--top--s">
        <Row justifyContent="end" alignItems="center">
          <Button
            kind="plain"
            size="xs"
            label={l10n.getString("filter-clear")}
            onClick={() => onChange(new Set())}
            type="button"
            testId="token-filter-clear"
          />
        </Row>
      </div>
    </div>
  );

  return (
    <Popover
      content={popoverContent}
      isOpen={isOpen}
      onUserDismiss={() => setIsOpen(false)}
      testId="token-filter-popover"
    >
      <Chip
        label={l10n.getString("filter-state-label")}
        count={
          selectedStates.size > 0 ? String(selectedStates.size) : undefined
        }
        onClick={() => setIsOpen((prev) => !prev)}
        endIcon={`chevron-${isOpen ? "up" : "down"}`}
      />
    </Popover>
  );
};

export default TokenStateFilter;
